import React, { useState } from 'react';
import {
  MAX_FIRMA,
  MAX_FUNKTION,
  MAX_NACHNAME,
  MAX_VORNAME,
  pruefeFormularFelder,
  type TeilnahmeFormular,
} from './einweisungTeilnahmeLogic';
import { EINGABE, EINGABE_FEHLER, FEHLERKASTEN, KNOPF_PRIMAER, KNOPF_SEKUNDAER } from '../../sifa/public/teilnahmeSeiteStil';

const VERSTANDEN_TEXT = 'Ich habe die Einweisung in den SiGe-Plan erhalten und verstanden.';

type Textfeld = Exclude<keyof TeilnahmeFormular, 'verstanden'>;

/**
 * Angaben zur Person: Vorname, Nachname, Firma (Pflicht), Funktion (frei)
 * und das Pflicht-Haekchen. Fehlertexte kommen aus pruefeFormularFelder und
 * erscheinen erst nach dem ersten Absenden — sonst meckert die Seite, bevor
 * der Teilnehmer ueberhaupt zu tippen anfaengt.
 */
export const EinweisungFormular: React.FC<{
  formular: TeilnahmeFormular;
  onAendern: <K extends keyof TeilnahmeFormular>(feld: K, wert: TeilnahmeFormular[K]) => void;
  onWeiter: () => void;
  onZurueck: () => void;
}> = ({ formular, onAendern, onWeiter, onZurueck }) => {
  const [abgeschickt, setAbgeschickt] = useState(false);

  const fehler = abgeschickt ? pruefeFormularFelder(formular) : {};
  const anzahlFehler = Object.keys(fehler).length;

  const absenden = (e: React.FormEvent) => {
    e.preventDefault();
    setAbgeschickt(true);
    if (Object.keys(pruefeFormularFelder(formular)).length === 0) onWeiter();
  };

  const feld = (name: Textfeld, label: string, autoComplete: string, maxLength: number, extra?: React.InputHTMLAttributes<HTMLInputElement>) => {
    const text = fehler[name];
    const id = `einweisung-${name}`;
    return (
      <div className="space-y-1.5">
        <label htmlFor={id} className="block text-base font-semibold text-slate-700">
          {label}
        </label>
        <input
          id={id}
          name={name}
          type="text"
          autoComplete={autoComplete}
          maxLength={maxLength}
          value={formular[name]}
          onChange={(e) => onAendern(name, e.target.value)}
          aria-invalid={text ? true : undefined}
          aria-describedby={text ? `${id}-fehler` : undefined}
          className={`${EINGABE} ${text ? EINGABE_FEHLER : ''}`}
          {...extra}
        />
        {text && (
          <p id={`${id}-fehler`} className="text-sm text-red-600">
            {text}
          </p>
        )}
      </div>
    );
  };

  const verstandenFehlt = !!fehler.verstanden;

  return (
    <form onSubmit={absenden} noValidate className="space-y-5">
      <div className="space-y-1">
        <h2 className="text-xl font-bold text-slate-900">Deine Angaben</h2>
        <p className="text-slate-600">Diese Angaben stehen in der Teilnehmerliste der Einweisung.</p>
      </div>

      {feld('vorname', 'Vorname', 'given-name', MAX_VORNAME)}
      {feld('nachname', 'Nachname', 'family-name', MAX_NACHNAME)}
      {feld('firma', 'Firma', 'organization', MAX_FIRMA)}
      {feld('funktion', 'Funktion (optional)', 'organization-title', MAX_FUNKTION, { placeholder: 'z. B. Polier, Bauleiter, Monteur' })}

      <div className="space-y-1.5">
        <label
          htmlFor="einweisung-verstanden"
          className={`flex items-start gap-3 p-4 min-h-[56px] rounded-xl border-2 cursor-pointer transition-all ${
            formular.verstanden ? 'border-blue-600 bg-blue-50' : verstandenFehlt ? 'border-red-400 bg-red-50' : 'border-slate-200 bg-white'
          }`}
        >
          <input
            id="einweisung-verstanden"
            name="verstanden"
            type="checkbox"
            checked={formular.verstanden}
            onChange={(e) => onAendern('verstanden', e.target.checked)}
            aria-invalid={verstandenFehlt ? true : undefined}
            aria-describedby={verstandenFehlt ? 'einweisung-verstanden-fehler' : undefined}
            className="mt-0.5 w-6 h-6 shrink-0 accent-blue-600"
          />
          <span className="text-base text-slate-800 leading-snug">{VERSTANDEN_TEXT}</span>
        </label>
        {verstandenFehlt && (
          <p id="einweisung-verstanden-fehler" className="text-sm text-red-600">
            {fehler.verstanden}
          </p>
        )}
      </div>

      {anzahlFehler > 0 && (
        <div className={FEHLERKASTEN} role="alert">
          Bitte prüfe die markierten Felder.
        </div>
      )}

      <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-between">
        <button type="button" onClick={onZurueck} className={KNOPF_SEKUNDAER}>
          Zurück
        </button>
        <button type="submit" className={KNOPF_PRIMAER}>
          Weiter zur Unterschrift
        </button>
      </div>
    </form>
  );
};
